import { useState } from 'react';
import { DatePicker, Modal } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { FileSpreadsheet, FileText, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  downloadDailySiteEngineerReport,
  type DailyReportFormat,
  type DailyReportScope,
} from '../../../../services/reportService';

interface DailyReportModalProps {
  open: boolean;
  onClose: () => void;
  /** Modal title. */
  title: string;
  /** One-line description of what the report covers (scope-aware copy). */
  description: string;
  /** Which download formats to offer (order preserved). */
  formats: DailyReportFormat[];
  /** Optional Performance Explorer drill scope; omit for portfolio-wide. */
  scope?: DailyReportScope;
}

const FORMAT_LABEL: Record<DailyReportFormat, string> = {
  pdf: 'Download PDF',
  xlsx: 'Download Excel',
};
const FORMAT_ICON: Record<DailyReportFormat, typeof FileText> = {
  pdf: FileText,
  xlsx: FileSpreadsheet,
};

/**
 * Shared date-picker modal + download flow behind both the portfolio-wide
 * "Download Daily Report" button and the scope-aware Performance Explorer
 * "Daily report" button. Date defaults to today; future dates are disabled.
 */
export default function DailyReportModal({
  open,
  onClose,
  title,
  description,
  formats,
  scope,
}: DailyReportModalProps) {
  const [date, setDate] = useState<Dayjs>(dayjs());
  const [busy, setBusy] = useState<DailyReportFormat | null>(null);

  const run = async (format: DailyReportFormat) => {
    setBusy(format);
    try {
      await downloadDailySiteEngineerReport(date.format('YYYY-MM-DD'), format, scope);
      toast.success(`Daily report downloaded — ${date.format('DD MMM YYYY')}`);
      onClose();
    } catch {
      toast.error('Report generation failed — please try again.');
    } finally {
      setBusy(null);
    }
  };

  const actionBtn = (format: DailyReportFormat) => {
    const Icon = FORMAT_ICON[format];
    return (
      <button
        key={format}
        type="button"
        disabled={busy !== null}
        onClick={() => run(format)}
        className="flex h-9 items-center gap-1.5 rounded-xl px-3.5 text-[11px] font-extrabold uppercase tracking-[0.08em] text-white transition hover:bg-[#2d5c47] disabled:opacity-60"
        style={{ backgroundColor: '#264f3c', border: '1px solid rgba(11,59,46,0.45)' }}
      >
        {busy === format ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <Icon className="h-3.5 w-3.5" />
        )}
        {FORMAT_LABEL[format]}
      </button>
    );
  };

  return (
    <Modal
      title={title}
      open={open}
      onCancel={() => {
        if (!busy) onClose();
      }}
      footer={<div className="flex justify-end gap-2">{formats.map(actionBtn)}</div>}
      destroyOnHidden
    >
      <p className="mb-3 text-sm text-[#606060]">Select a date to generate the report.</p>
      <DatePicker
        value={date}
        onChange={(d) => d && setDate(d)}
        allowClear={false}
        disabledDate={(d) => d.isAfter(dayjs(), 'day')}
        format="DD MMM YYYY"
        className="w-full"
      />
      <p className="mt-3 text-xs text-[#8A8A8A]">{description}</p>
    </Modal>
  );
}
